import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Building2, Check, LogOut, X } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/lib/api/client';

interface Membership {
  id: string;
  tenantId: string;
  tenantName: string;
  tenantLogo: string | null;
  status: 'INVITED' | 'ACTIVE' | 'INACTIVE';
  joinedAt: string | null;
}

export default function OrganizationsPage() {
  const { t } = useTranslation('scout');
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [confirmLeave, setConfirmLeave] = useState<Membership | null>(null);

  const fetchMemberships = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await api.get<{ items: Membership[] }>('/scout/memberships');
      setMemberships(data.items);
    } catch {
      setError(t('organizations.loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    fetchMemberships();
  }, [isAuthenticated, navigate, fetchMemberships]);

  // Escape key to close modal
  useEffect(() => {
    if (!confirmLeave) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setConfirmLeave(null);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [confirmLeave]);

  const handleAccept = async (membershipId: string) => {
    try {
      setActionLoading(membershipId);
      await api.patch(`/scout/memberships/${membershipId}/accept`);
      navigate('/', { replace: true });
    } catch {
      setError(t('organizations.actionError'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleLeave = async (membership: Membership) => {
    try {
      setActionLoading(membership.id);
      setConfirmLeave(null);
      await api.post(`/scout/memberships/${membership.id}/leave`);
      await fetchMemberships();
    } catch {
      setError(t('organizations.actionError'));
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-forest-wash text-forest';
      case 'INVITED':
        return 'bg-gold/10 text-gold';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/5 to-white pb-8">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-full hover:bg-warm-faint"
          aria-label={t('common.goBack')}
        >
          <ArrowLeft className="w-5 h-5 text-muted-foreground" />
        </button>
        <h1 className="text-section font-semibold text-foreground">{t('organizations.title')}</h1>
      </div>

      <div className="px-4 space-y-3">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
          </div>
        ) : error ? (
          <div className="text-center py-12 text-muted-foreground text-small">{error}</div>
        ) : memberships.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground text-small">{t('organizations.empty')}</div>
        ) : (
          memberships.map((m) => (
            <div
              key={m.id}
              className="bg-white rounded-xl p-4 shadow-sm border border-warm-border flex items-center gap-3"
            >
              <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0 overflow-hidden">
                {m.tenantLogo ? (
                  <img src={m.tenantLogo} alt="" className="w-full h-full object-contain" />
                ) : (
                  <Building2 className="w-5 h-5 text-steel" />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <p className="font-medium text-foreground text-small truncate">{m.tenantName}</p>
                <span
                  className={`inline-block text-caption px-2 py-0.5 rounded-full font-medium mt-1 ${getStatusColor(m.status)}`}
                >
                  {m.status === 'ACTIVE'
                    ? t('organizations.active')
                    : m.status === 'INVITED'
                      ? t('organizations.invited')
                      : m.status}
                </span>
              </div>

              {m.status === 'INVITED' && (
                <button
                  onClick={() => handleAccept(m.id)}
                  disabled={actionLoading === m.id}
                  className="px-3 py-1.5 bg-primary text-white text-caption font-medium rounded-lg hover:bg-forest disabled:opacity-50 flex items-center gap-1"
                >
                  <Check className="w-3.5 h-3.5" />
                  {t('organizations.accept')}
                </button>
              )}
              {m.status === 'ACTIVE' && (
                <button
                  onClick={() => setConfirmLeave(m)}
                  disabled={actionLoading === m.id}
                  className="px-3 py-1.5 bg-muted text-muted-foreground text-caption font-medium rounded-lg hover:bg-warm-faint disabled:opacity-50 flex items-center gap-1"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  {t('organizations.leave')}
                </button>
              )}
            </div>
          ))
        )}
      </div>

      {/* Leave confirmation modal */}
      {confirmLeave && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-lg bg-white rounded-t-2xl p-6 animate-slide-up">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-section font-bold text-foreground">{t('organizations.leave')}</h3>
              <button
                onClick={() => setConfirmLeave(null)}
                className="p-1 rounded-lg hover:bg-warm-faint"
                aria-label={t('common.close')}
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>
            <p className="text-small text-muted-foreground mb-6">{t('organizations.leaveConfirm')}</p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmLeave(null)}
                className="flex-1 py-3 rounded-xl border border-warm-border text-foreground font-medium text-small"
              >
                {t('status.cancel')}
              </button>
              <button
                onClick={() => handleLeave(confirmLeave)}
                className="flex-1 py-3 rounded-xl bg-critical text-white font-medium text-small"
              >
                {t('organizations.leave')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
